import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getCompanyIntegrations, getIntegrationHealthHistory } from '../api';
import { INTEGRATIONS_CATALOG } from '../components/Integrations/mockData';
import { Button, Pagination } from '@/components';
import { DashboardPage } from '../components/PremiumDesign';
import { SkeletonText } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';

export function IntegrationHistoryPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [historyPage, setHistoryPage] = useState(1);
  const itemsPerPage = 20;

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  // Find app details in mock catalog data
  const allApps = [
    ...INTEGRATIONS_CATALOG.messengers,
    ...(INTEGRATIONS_CATALOG.providers || []),
  ];
  const app =
    allApps.find((a) => a.id === appId) ||
    (appId === 'instagram-business'
      ? allApps.find((a) => a.id === 'instagram')
      : null) ||
    (appId === 'google-workspace' || appId === 'google_workspace'
      ? allApps.find((a) => a.id === 'google_workspace')
      : null) ||
    (appId === 'other-email' || appId === 'other_email'
      ? allApps.find((a) => a.id === 'other_email')
      : null);

  // Query: Fetch company integrations status
  const {
    data: companyIntegrations = [],
    isLoading: isIntegrationsApiLoading,
  } = useQuery({
    queryKey: ['company-integrations', companyId],
    queryFn: () => getCompanyIntegrations(companyId),
    enabled: !!companyId,
  });

  const activeIntegration =
    companyIntegrations?.find(
      (item) =>
        item.integration_slug === 'whatsapp' &&
        (appId === 'whatsapp-business' || appId === 'whatsapp-business-prov'),
    ) ||
    companyIntegrations?.find(
      (item) =>
        item.integration_slug === 'instagram' &&
        (appId === 'instagram-business' || appId === 'instagram'),
    ) ||
    companyIntegrations?.find(
      (item) =>
        item.integration_slug === 'google_workspace' &&
        (appId === 'google-workspace' || appId === 'google_workspace'),
    ) ||
    companyIntegrations?.find(
      (item) =>
        item.integration_slug === 'other_email' &&
        (appId === 'other-email' || appId === 'other_email'),
    ) ||
    companyIntegrations?.find((item) => item.integration_slug === appId);

  const companyIntegrationId = activeIntegration?.id;

  // Query: Fetch integration health status history
  const {
    data: healthHistoryData,
    isLoading: isHistoryInitialLoading,
    isFetching: isHistoryFetching,
    isError: isHistoryError,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: [
      'integration-health-history',
      companyId,
      companyIntegrationId,
      historyPage,
    ],
    queryFn: () =>
      getIntegrationHealthHistory({
        companyId,
        companyIntegrationId,
        params: { page: historyPage },
      }),
    enabled: !!companyId && !!companyIntegrationId && !!app,
    placeholderData: keepPreviousData,
  });

  const isIntegrationsLoading = useDelayedSkeleton(isIntegrationsApiLoading);
  const showDelayedSkeleton = useDelayedSkeleton(
    isHistoryFetching && healthHistoryData !== undefined,
    300,
    500,
  );
  const isHistoryLoading = isHistoryInitialLoading || showDelayedSkeleton;

  const totalCount = healthHistoryData?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const prevTotalPagesRef = useRef(totalPages);

  useEffect(() => {
    if (healthHistoryData && totalCount >= 0) {
      if (historyPage > totalPages) {
        setHistoryPage(totalPages);
      }
      prevTotalPagesRef.current = totalPages;
    }
  }, [totalCount, historyPage, totalPages, healthHistoryData]);

  useEffect(() => {
    if (isHistoryError && historyError) {
      const errorMsg =
        historyError?.response?.data?.detail || historyError?.message || '';
      if (errorMsg.toLowerCase().includes('invalid page')) {
        toast('Requested page is not available.', {
          icon: 'ℹ️',
          style: { fontSize: '13px', fontWeight: 500 },
        });
        setHistoryPage(prevTotalPagesRef.current);
      }
    }
  }, [isHistoryError, historyError]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (prevTotalPagesRef.current || totalPages)) {
      return;
    }
    setHistoryPage(newPage);
  };

  if (!app) {
    return (
      <DashboardPage>
        <div className="w-full flex flex-col items-center justify-center min-h-[300px] gap-4 font-sf-pro-display text-center">
          <h2 className="text-xl font-bold text-text-primary mb-2">
            Integration Not Found
          </h2>
          <Button
            variant="primary"
            onClick={() => navigate('/integrations')}
            className="px-6 h-[44px]"
          >
            Back to Catalog
          </Button>
        </div>
      </DashboardPage>
    );
  }

  return (
    <DashboardPage>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (
                appId === 'google-workspace' ||
                appId === 'google_workspace'
              ) {
                navigate('/integrations/google-workspace');
              } else if (
                appId === 'instagram-business' ||
                appId === 'instagram'
              ) {
                navigate('/integrations/instagram-business');
              } else {
                navigate(`/integrations/${appId}`);
              }
            }}
            className="w-9 h-9 rounded-lg border border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 bg-white shadow-sm flex items-center justify-center transition-colors focus:outline-none shrink-0"
            title="Back to Details"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight">
            {(activeIntegration?.integration_name || app.name).replace(
              /Other Email/gi,
              'Business Email',
            )}{' '}
            Health History
          </h1>
        </div>

        {/* Total Logs Pill */}
        <div className="flex items-center">
          <div className="bg-[#eef5ef] text-[#4CAF50] px-4 py-2 rounded-full text-[13px] font-medium flex items-center gap-2 border border-[#4CAF50]/10 shadow-sm">
            <div className="w-2 h-2 bg-[#4CAF50] rounded-full"></div>
            <span className="text-gray-600">Total Checked Logs :</span>
            <span className="font-bold text-gray-900 ml-1">{totalCount}</span>
          </div>
        </div>
      </div>

      {/* History content list table */}
      <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden shrink min-h-0">
        <div className="w-full overflow-auto custom-scrollbar shrink min-h-0">
          {isHistoryError ? (
            <div className="w-full p-8 bg-red-50 flex flex-col items-center justify-center gap-3 text-center border-b border-gray-100">
              <div className="text-red-600 text-[14px] font-semibold">
                Failed to fetch health check history
              </div>
              <p className="text-[13px] text-red-500/80 max-w-sm">
                {historyError?.response?.data?.detail ||
                  historyError?.message ||
                  'Error occurred while contacting the history endpoint.'}
              </p>
              <Button
                variant="primary"
                onClick={() => refetchHistory()}
                className="py-2 px-5 h-[38px] text-[13px]"
              >
                Retry Check
              </Button>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#1b2520] text-gray-200 text-[13px] font-medium tracking-wide border-b-0">
                  <th className="py-4 px-6 font-medium whitespace-nowrap">
                    Status
                  </th>
                  <th className="py-4 px-6 font-medium whitespace-nowrap">
                    Created Date
                  </th>
                  <th className="py-4 px-6 font-medium whitespace-nowrap">
                    Failure Reason
                  </th>
                  <th className="py-4 px-6 font-medium text-center whitespace-nowrap">
                    Token Check
                  </th>
                  <th className="py-4 px-6 font-medium text-center whitespace-nowrap">
                    Connect Check
                  </th>
                  <th className="py-4 px-6 font-medium text-center whitespace-nowrap">
                    Scope Check
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {isIntegrationsLoading ||
                isHistoryLoading ||
                !healthHistoryData ? (
                  [...Array(10)].map((_, i) => (
                    <tr
                      key={`skeleton-${i}`}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <SkeletonText
                          width="w-20"
                          height="h-5"
                          className="rounded-full"
                        />
                      </td>
                      <td className="py-4 px-6">
                        <SkeletonText width="w-32" height="h-4" />
                      </td>
                      <td className="py-4 px-6">
                        <SkeletonText width="w-48" height="h-4" />
                      </td>
                      <td className="py-4 px-6">
                        <SkeletonText
                          width="w-12"
                          height="h-4"
                          className="mx-auto"
                        />
                      </td>
                      <td className="py-4 px-6">
                        <SkeletonText
                          width="w-12"
                          height="h-4"
                          className="mx-auto"
                        />
                      </td>
                      <td className="py-4 px-6">
                        <SkeletonText
                          width="w-12"
                          height="h-4"
                          className="mx-auto"
                        />
                      </td>
                    </tr>
                  ))
                ) : !healthHistoryData?.results ||
                  healthHistoryData.results.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-16 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <div className="w-16 h-16 bg-gray-50 border border-gray-100 rounded-full flex items-center justify-center mx-auto">
                          <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                        </div>
                        <span className="text-[14px] font-medium text-gray-600">
                          No health check history found for this integration.
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  (healthHistoryData?.results || []).map((item, idx) => {
                    const isHealthy =
                      item.health_status?.toLowerCase() === 'healthy';
                    const checkResults = item.check_results || {};

                    return (
                      <tr
                        key={item.id || idx}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-4 px-6">
                          <span
                            className={`inline-flex items-center px-3 py-1 text-[11px] font-semibold rounded-full ${
                              isHealthy
                                ? 'text-[#4CAF50] bg-[#eef5ef]'
                                : 'text-rose-600 bg-rose-50'
                            }`}
                          >
                            {item.health_status_label ||
                              item.health_status ||
                              'Unhealthy'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-[13px] text-text-muted font-medium whitespace-nowrap">
                          {new Date(item.checked_at).toLocaleString('en-US', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </td>
                        <td
                          className="py-4 px-6 text-[13px] text-gray-500 truncate max-w-[200px]"
                          title={item.failure_reason}
                        >
                          {item.failure_reason || '-'}
                        </td>
                        <td className="py-4 px-6 text-center text-[13px] font-medium">
                          <span
                            className={
                              checkResults.token_valid
                                ? 'text-[#4CAF50]'
                                : 'text-rose-500'
                            }
                          >
                            {checkResults.token_valid ? 'Valid' : 'Invalid'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center text-[13px] font-medium">
                          <span
                            className={
                              checkResults.connectivity_ok
                                ? 'text-[#4CAF50]'
                                : 'text-rose-500'
                            }
                          >
                            {checkResults.connectivity_ok ? 'Ok' : 'Error'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center text-[13px] font-medium">
                          <span
                            className={
                              checkResults.permissions_ok
                                ? 'text-[#4CAF50]'
                                : 'text-rose-500'
                            }
                          >
                            {checkResults.permissions_ok ? 'Ok' : 'Fail'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {!isIntegrationsLoading && !isHistoryLoading && !isHistoryError && (
          <div className="p-4 border-t border-gray-100 flex justify-center">
            <Pagination
              currentPage={historyPage}
              totalCount={totalCount}
              itemsPerPage={itemsPerPage}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </DashboardPage>
  );
}
